import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, filter } from 'rxjs';

import { Category } from '@fcom/dapi/api/models';
import { ButtonMode, ButtonPosition, ButtonTheme, ModalButtons, RadioButtonTheme } from '@fcom/ui-components/index';
import { unsubscribe } from '@fcom/core/index';
import { LanguageService } from '@fcom/ui-translate/index';

import { ExitRowSeatInfo, ExitRowSeatsConfirmation } from '../../../interfaces/checkin.interface';

interface PassengerSeats {
  id: string;
  firstName: string;
  lastName: string;
}

interface ExitRowSeatConfirmationStatus {
  value: boolean;
  strValue: string;
  label: string;
}

const MEET_REQUIREMENTS = 'meetRequirements';
const NOT_MEET_REQUIREMENTS = 'notMeetRequirements';

type ExitRowSeatConfirmationStatusNames = 'meetRequirements' | 'notMeetRequirements';

@Component({
  selector: 'fin-check-in-exit-row-seats',
  templateUrl: 'check-in-exit-row-seats.component.html',
  styleUrls: ['./check-in-exit-row-seats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInExitRowSeatsComponent implements OnInit, OnDestroy {
  readonly Category = Category;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ModalButtons = ModalButtons;
  readonly ButtonMode = ButtonMode;
  readonly RadioButtonTheme = RadioButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  exitRowSeats: ExitRowSeatInfo[];

  @Output()
  confirmExitRowSeats: EventEmitter<ExitRowSeatsConfirmation> = new EventEmitter<ExitRowSeatsConfirmation>();

  reactiveForm: UntypedFormGroup;
  passengersWithExitSeats: PassengerSeats[];
  exitRowSeatConfirmationStatuses: Record<ExitRowSeatConfirmationStatusNames, ExitRowSeatConfirmationStatus> = {
    meetRequirements: {
      value: true,
      strValue: MEET_REQUIREMENTS,
      label: 'MMB.checkIn.exitRowSeats.optionLabels.meetRequirements',
    },
    notMeetRequirements: {
      value: false,
      strValue: NOT_MEET_REQUIREMENTS,
      label: 'MMB.checkIn.exitRowSeats.optionLabels.notMeetRequirements',
    },
  };
  notMeetRequirementsChecked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  exitRowSeatRequirementsArticle$: Observable<string>;
  subscriptions: Subscription = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.exitRowSeatRequirementsArticle$ = this.languageService
      .translate('MMB.fragments.checkIn.exitRowSeats.requirementsList.url')
      .pipe(
        distinctUntilChanged(),
        filter((requirementsList) => !!requirementsList)
      );
    this.reactiveForm = this.fb.group({});
    this.passengersWithExitSeats = this.createUniqueExitRowSeatPassengersList();
    this.passengersWithExitSeats.forEach((pax) =>
      this.reactiveForm.addControl(pax.id, new FormControl(null, Validators.required))
    );
    this.subscriptions.add(
      this.reactiveForm.valueChanges.subscribe((value) =>
        this.notMeetRequirementsChecked$.next(this.isNotMeetRequirementsChecked(value))
      )
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onConfirmExitRowSeats(): void {
    const confirmedExitSeatPax = [];
    if (this.reactiveForm.valid) {
      for (const paxId in this.reactiveForm.value) {
        if (this.getExitRowSeatConfirmationValue(paxId)) {
          confirmedExitSeatPax.push(paxId);
        }
      }
      this.confirmExitRowSeats.emit({ confirmedPax: confirmedExitSeatPax });
    } else {
      this.reactiveForm.markAllAsTouched();
    }
  }

  private isNotMeetRequirementsChecked(form: Record<string, string>): boolean {
    return Object.keys(form).some((formKey) => form[formKey] === NOT_MEET_REQUIREMENTS);
  }

  private createUniqueExitRowSeatPassengersList(): PassengerSeats[] {
    return this.exitRowSeats
      .flatMap((exitRowSeatFlight) => exitRowSeatFlight.passengers)
      .map((pax) => {
        return {
          id: pax.id,
          firstName: pax.firstName,
          lastName: pax.lastName,
        };
      })
      .filter((pax, i, arr) => arr.findIndex((currentPax) => currentPax.id === pax.id) === i);
  }

  private getExitRowSeatConfirmationValue(paxId: string) {
    return this.exitRowSeatConfirmationStatuses[this.reactiveForm.value[paxId]].value;
  }
}
